import { useState } from "react";
import { createPortal } from "react-dom";
import "./LoginModal.css";

interface LoginModalProps {
    onClose: () => void;
    onLogin: (email: string, password: string) => Promise<void>;
}

export function LoginModal({ onClose, onLogin }: LoginModalProps) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!email.trim() || !password.trim()) {
            setError("Please input all required fields");
            return;
        }

        setLoading(true);

        try {
            await onLogin(email, password);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (loading) return;
        onClose();
    };

    return createPortal(
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={handleCancel}>
            <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">Login</h2>
                    <button className="modal-close" type="button" onClick={handleCancel} aria-label="Close">
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <label className="modal-label" htmlFor="login-email">
                            Email *
                        </label>
                        <input
                            id="login-email"
                            className="modal-input"
                            type="text"
                            placeholder="Your email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (error) setError("");
                            }}
                            disabled={loading}
                        />

                        <label className="modal-label" htmlFor="login-password">
                            Password *
                        </label>
                        <input
                            id="login-password"
                            className="modal-input"
                            type="password"
                            placeholder="Your password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (error) setError("");
                            }}
                            disabled={loading}
                        />
                    </div>

                    <div className="modal-actions">
                        {error && <div className="login-error">{error}</div>}
                        <div className="modal-buttons">
                            <button
                                className="modal-btn modal-btn-secondary"
                                type="button"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                className="modal-btn modal-btn-primary"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Logging in..." : "Login"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}
